import {
  RecoveryLeaderboard, SleepLeaderboard, StrainLeaderboard, TrainingZoneLeaderboard,
} from 'api/widgetApi';
import { useMemo } from 'react';
import {
  CellProps,
  Column, useSortBy, useTable,
} from 'react-table';
import { widgetNameAvatarCell } from 'tableUtils/tableCells';
import { compareDecimals } from 'progress/breakdown/cells';
import {
  minutesToHoursCell, recoveryCell, roundedNumberCell, strainCell,
} from './leaderboardTableCells';
import { WidgetType } from '../widget/widget';

const nameColumn = {
  Header: 'Leaderboard', accessor: 'name', disableSortBy: true, Cell: widgetNameAvatarCell,
};

const recoveryColumn = {
  Header: 'Recovery',
  accessor: 'recovery',
  sortType: 'alphanumeric',
  Cell: (cellProps: CellProps<RecoveryLeaderboard>) => recoveryCell(cellProps.value),
};

export const recoveryBaseColumns = [nameColumn, recoveryColumn];

export const recoveryExpandedColumns = [nameColumn,
  {
    Header: 'RHR',
    accessor: 'rhr',
    sortType: 'alphanumeric',
    Cell: (cellProps: CellProps<
    RecoveryLeaderboard
    >) => roundedNumberCell(cellProps.value, WidgetType.RECOVERY),
  },
  {
    Header: 'HRV',
    accessor: 'hrv',
    sortType: 'alphanumeric',
    Cell: (cellProps: CellProps<
    RecoveryLeaderboard
    >) => roundedNumberCell(cellProps.value, WidgetType.RECOVERY),
  },
  recoveryColumn,
];

const sleepPerformanceColumn = {
  Header: 'Performance',
  accessor: 'sleep_performance',
  sortType: 'alphanumeric',
  Cell: (cellProps: CellProps<
  RecoveryLeaderboard
  >) => roundedNumberCell(cellProps.value, WidgetType.SLEEP_PERFORMANCE, true),
};

const hoursOfSleepColumn = {
  Header: 'Hours of Sleep',
  accessor: 'sleep_duration_minutes',
  sortType: 'alphanumeric',
  Cell: (cellProps: CellProps<SleepLeaderboard>) => minutesToHoursCell(cellProps.value),
};

const sleepDebtColumn = {
  Header: 'Sleep Debt',
  accessor: 'sleep_debt_minutes',
  sortType: 'alphanumeric',
  Cell: (cellProps: CellProps<SleepLeaderboard>) => minutesToHoursCell(cellProps.value),
};

export const sleepPerformanceBaseColumns = [nameColumn, sleepPerformanceColumn];

export const sleepPerformanceExpandedColumns = [nameColumn,
  hoursOfSleepColumn,
  sleepDebtColumn,
  sleepPerformanceColumn,
];

export const sleepDebtBaseColumns = [nameColumn, sleepDebtColumn];

export const sleepDebtExpandedColumns = [
  nameColumn,
  hoursOfSleepColumn,
  sleepPerformanceColumn,
  sleepDebtColumn,
];

const strainColumn = {
  Header: 'Strain',
  accessor: 'strain',
  sortType: compareDecimals,
  Cell: (cellProps: CellProps<StrainLeaderboard>) => strainCell(cellProps.value),
};

export const strainBaseColumns = [nameColumn, strainColumn];

export const strainExpandedColumns = [nameColumn,
  {
    Header: 'Avg. HR',
    accessor: 'hr',
    sortType: 'alphanumeric',
    Cell: (cellProps: CellProps<
    StrainLeaderboard
    >) => roundedNumberCell(cellProps.value, WidgetType.DAY_STRAIN),
  },
  {
    Header: 'Calories',
    accessor: 'calories',
    sortType: 'alphanumeric',
    Cell: (cellProps: CellProps<
    StrainLeaderboard
    >) => roundedNumberCell(cellProps.value, WidgetType.DAY_STRAIN),
  },
  strainColumn,
];

const trainingZonesNameColumn = {
  Header: '', accessor: 'name', disableSortBy: true, Cell: widgetNameAvatarCell,
};

export const trainingZoneBaseColumns = [trainingZonesNameColumn];

export const trainingZoneExpandedColumns = [trainingZonesNameColumn,
  recoveryColumn,
  strainColumn,
];

export type LeaderboardTableRow = {
  user_id: number;
  opted_in: boolean;
  training_zone?: string;
  recovery?: number;
  strain?: number;
};

function useLeaderboardTable(
  data: RecoveryLeaderboard[] |
  StrainLeaderboard[] | SleepLeaderboard[] | TrainingZoneLeaderboard[],
  columns: Column[],
  defaultSortById: string,
) {
  // Memoizing as react-table suggests this
  const columnsToUse = useMemo(
    () => columns as Column<LeaderboardTableRow>[],
    [columns],
  );

  return {
    ...useTable(
      {
        columns: columnsToUse,
        data,
        disableSortRemove: true,
        initialState: {
          sortBy: [
            {
              id: defaultSortById,
              desc: true,
            },
          ],
        },
      },
      useSortBy,
    ),
  };
}

export default useLeaderboardTable;
